const {
  personSmsFlagOn,
  personChatNotifyFlagOn,
} = require('./peopleListFilter');

function personFlagSpan(letter, title, on) {
  const onClass = on ? ' person-flag-on' : '';
  return `<span class="person-flag${onClass}" title="${title}">${letter}</span>`;
}

/** S and N circles beside a Review tenant name. Same marks as People Search. */
function reviewPersonFlagHtml(request) {
  return (
    '<span class="person-flags">' +
    personFlagSpan('S', 'SMS opt-in', personSmsFlagOn(request)) +
    personFlagSpan('N', 'Chat notifications', personChatNotifyFlagOn(request)) +
    '</span>'
  );
}

/** Same grouping key as list-view “location” mode and the Review dropdown. */
function reviewLocationKeyForOrder(order) {
  return String(order?.parentId || order?.propertyId || 0);
}

function reviewSearchText(value) {
  return String(value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Blank pattern matches. Otherwise every word must appear in the work order. */
function workOrderMatchesReviewSearch(order, pattern) {
  const terms = String(pattern ?? '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((term) => (term.startsWith('#') ? term.slice(1) : term))
    .filter(Boolean);
  if (!terms.length) return true;
  if (!order) return false;
  const hay = [
    order.id,
    order.property_address,
    order.parentAddress,
    order.problem_description,
    order.name,
  ]
    .map(reviewSearchText)
    .join(' ');
  return terms.every((term) => hay.includes(term));
}

function formatPropertyAddressLine(request, options = {}) {
  const address = request?.property_address || '';
  const addressCheck = request?.propertyId ? ' ✓' : '';
  const addressHtml = options.clickableAddress
    ? `<span class="wo-property-address">${address}</span>`
    : address;
  return `**Property Address:** ${addressHtml}${addressCheck}`;
}

function parseReviewWorkOrderId(value) {
  const digits = String(value ?? '').replace(/[^\d]/g, '');
  if (!digits) return null;
  const n = Number(digits);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** `?search=:5900` is a link to one work order. Other searches stay text filters. */
function workOrderIdFromSearchQuery(search) {
  const raw = String(search ?? '').trim();
  if (!/^:\d+$/.test(raw)) return null;
  return parseReviewWorkOrderId(raw);
}

/** `?search=:id` opens that work order in Review. A text search stays on the classic list. */
function shouldOpenReviewForWorkOrderLink(search) {
  return workOrderIdFromSearchQuery(search) != null;
}

/** A completed or deleted linked order is finished. Review only lists open orders. */
function workOrderLinkIsFinished(order) {
  if (!order) return false;
  return order.completedOn != null || Boolean(order.deleted);
}

function workOrderLinkDeleted(order) {
  const flag = order && order.deleted;
  return flag === 1 || flag === true || flag === '1';
}

/**
 * Replace one cached work order with the row just loaded for a direct link.
 * A newer modifiedOn already in the list wins, so a later sync is not rolled back.
 */
function mergeWorkOrderLinkRefresh(list, fresh) {
  const rows = Array.isArray(list) ? list.slice() : [];
  if (!fresh || fresh.id == null) return rows;
  const id = Number(fresh.id);
  const idx = rows.findIndex((row) => Number(row && row.id) === id);
  if (workOrderLinkDeleted(fresh)) {
    if (idx >= 0) rows.splice(idx, 1);
    return rows;
  }
  if (idx < 0) {
    rows.push({ ...fresh });
    return rows;
  }
  const existingTime = Date.parse(rows[idx].modifiedOn);
  const freshTime = Date.parse(fresh.modifiedOn);
  if (
    Number.isFinite(existingTime) &&
    Number.isFinite(freshTime) &&
    existingTime > freshTime
  ) {
    return rows;
  }
  rows[idx] = { ...rows[idx], ...fresh };
  return rows;
}

/** `?review=5900` opens that work order in Review, including the localhost mic handoff. */
function workOrderIdFromReviewQuery(value) {
  const raw = Array.isArray(value) ? value[0] : value;
  const text = String(raw ?? '').trim();
  if (!/^\d+$/.test(text)) return null;
  return parseReviewWorkOrderId(text);
}

function reviewVoiceLocalhostUrl(workOrderId) {
  const id = parseReviewWorkOrderId(workOrderId);
  if (id == null) return null;
  return `http://localhost:8080/mworkorders?review=${id}`;
}

function reviewVoicePageCanRecord(hostname, isSecureContext) {
  const host = String(hostname || '').toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') {
    return true;
  }
  return Boolean(isSecureContext);
}

/** Desktop Review uses the same cutoff as the work-order Close button. */
const REVIEW_DESKTOP_MIN_WIDTH = 501;

/**
 * Escape resets Review filters only on desktop, while the pointer is over
 * the open Review panel, and no dialog is covering that panel.
 */
function reviewEscapeResetsToDefault({
  showReviewPopup,
  pointerOverReview,
  width,
  overlayOpen,
} = {}) {
  if (!showReviewPopup || !pointerOverReview || overlayOpen) return false;
  const n = Number(width);
  return Number.isFinite(n) && n >= REVIEW_DESKTOP_MIN_WIDTH;
}

/** Which work order Review should open on. The stored back target is not used. */
function reviewOpenFocusId({ explicitFocusId, selectedId } = {}) {
  if (explicitFocusId != null && explicitFocusId !== '') {
    return Number(explicitFocusId);
  }
  if (selectedId != null && selectedId !== '') return Number(selectedId);
  return null;
}

function newestWorkOrderIndex(list) {
  const rows = Array.isArray(list) ? list : [];
  let best = 0;
  for (let i = 1; i < rows.length; i += 1) {
    if (Number(rows[i]?.id) > Number(rows[best]?.id)) best = i;
  }
  return best;
}

function reviewDetailsInteractiveTarget(event) {
  const el = event && event.target;
  if (!el || typeof el.closest !== 'function') return null;
  if (el.closest('.wo-person-name')) return 'person';
  if (el.closest('.wo-property-address')) return 'address';
  if (el.closest('.wo-scheduled-date')) return 'scheduled';
  return null;
}

module.exports = {
  reviewLocationKeyForOrder,
  workOrderMatchesReviewSearch,
  reviewPersonFlagHtml,
  formatPropertyAddressLine,
  reviewDetailsInteractiveTarget,
  parseReviewWorkOrderId,
  workOrderIdFromSearchQuery,
  workOrderIdFromReviewQuery,
  reviewVoiceLocalhostUrl,
  reviewVoicePageCanRecord,
  shouldOpenReviewForWorkOrderLink,
  workOrderLinkIsFinished,
  mergeWorkOrderLinkRefresh,
  reviewOpenFocusId,
  newestWorkOrderIndex,
  REVIEW_DESKTOP_MIN_WIDTH,
  reviewEscapeResetsToDefault,
};

